import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Check, X } from 'lucide-react'
import { getTypeOfInsuranceGroups } from '@/screens/scan/api'
import { EMPTY_FILTERS, type MatchMode, type ScanFilters } from '@/screens/scan/scanCache'
import { renderStateName } from '@/screens/scan/states'
import { normalizeLabel } from '@/screens/scan/lib/labels'
import {
    ARCHIVED_OPTIONS,
    BUSINESS_TYPE_OPTIONS,
    MATCH_MODE_OPTIONS,
    buildOptions
} from '@/screens/scan/lib/options'
import type { UseChecklistScan } from '@/screens/scan/useChecklistScan'
import { FilterField } from './FilterField'
import { MultiSelect } from './MultiSelect'
import { Segmented } from './Segmented'
import { TagInput } from './TagInput'

/** Filters sub-view, mirroring the web app's ChecklistItemSelectorDialog filters. */
export function FiltersPanel({ scan, onClose }: { scan: UseChecklistScan; onClose: () => void }) {
  const [draft, setDraft] = useState<ScanFilters>(scan.filters)
  const [insuranceGroupOptions, setInsuranceGroupOptions] = useState<
    Array<{ value: string; label: string }>
  >([])

  const set = <K extends keyof ScanFilters>(key: K, value: ScanFilters[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  const setMatchMode = (field: 'names' | 'topics', mode: MatchMode) =>
    setDraft((prev) => ({
      ...prev,
      matchModes: { ...prev.matchModes, [field]: mode }
    }))

  useEffect(() => {
    let cancelled = false
    getTypeOfInsuranceGroups()
      .then((groups) => {
        if (cancelled) return
        setInsuranceGroupOptions(
          groups
            .map((group) => ({ value: group._id, label: group.name }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      })
      .catch(() => {
        if (!cancelled) setInsuranceGroupOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  // When Business Type is set, TOI / Sub-TOI options narrow to matching items
  // (mirrors web clearDependentFields + getRampFilter*Options(businessTypes)).
  const itemsForToi = useMemo(() => {
    if (!draft.businessTypes.length) return scan.items
    return scan.items.filter(
      (item) => item.businessType && draft.businessTypes.includes(item.businessType)
    )
  }, [scan.items, draft.businessTypes])

  const typeOptions = useMemo(
    () => buildOptions(scan.items, (item) => (item.type ? [item.type] : []), normalizeLabel),
    [scan.items]
  )
  const stateOptions = useMemo(
    () => buildOptions(scan.items, (item) => item.states ?? [], renderStateName),
    [scan.items]
  )
  const scopeOptions = useMemo(
    () => buildOptions(scan.items, (item) => (item.scope ? [item.scope] : []), normalizeLabel),
    [scan.items]
  )
  const toiOptions = useMemo(
    () =>
      buildOptions(
        itemsForToi,
        (item) => item.typeOfInsurances ?? [],
        (value) => value
      ),
    [itemsForToi]
  )
  const subToiOptions = useMemo(
    () =>
      buildOptions(
        itemsForToi,
        (item) => item.subTypeOfInsurances ?? [],
        (value) => value
      ),
    [itemsForToi]
  )

  return (
    <>
      <div className='ramp-scan-head'>
        <button type='button' className='ramp-link ramp-scan-back' onClick={onClose}>
          <ArrowLeft size={14} /> Checklist items
        </button>
        <div className='ramp-scan-title'>Filters</div>
      </div>

      <div className='ramp-scan-list ramp-filters'>
        <FilterField label='Is Archived'>
          <Segmented
            options={ARCHIVED_OPTIONS}
            value={draft.archived}
            onChange={(value) => set('archived', value as ScanFilters['archived'])}
          />
        </FilterField>
        <FilterField label='Type'>
          <MultiSelect
            placeholder='Select…'
            options={typeOptions}
            selected={draft.types}
            onChange={(value) => set('types', value)}
          />
        </FilterField>
        <FilterField label='Name'>
          <Segmented
            className='ramp-seg--match'
            options={MATCH_MODE_OPTIONS}
            value={draft.matchModes.names ?? 'cn'}
            onChange={(value) => setMatchMode('names', value as MatchMode)}
          />
          <TagInput values={draft.names} onChange={(value) => set('names', value)} />
        </FilterField>
        <FilterField label='Topic'>
          <Segmented
            className='ramp-seg--match'
            options={MATCH_MODE_OPTIONS}
            value={draft.matchModes.topics ?? 'cn'}
            onChange={(value) => setMatchMode('topics', value as MatchMode)}
          />
          <TagInput values={draft.topics} onChange={(value) => set('topics', value)} />
        </FilterField>
        <FilterField label='State'>
          <MultiSelect
            placeholder='Select…'
            options={stateOptions}
            selected={draft.states}
            onChange={(value) => set('states', value)}
          />
        </FilterField>
        <FilterField label='Scope'>
          <MultiSelect
            placeholder='Select…'
            options={scopeOptions}
            selected={draft.scopes}
            onChange={(value) => set('scopes', value)}
          />
        </FilterField>
        <FilterField label='Insurance Groups'>
          <MultiSelect
            placeholder='Select…'
            options={insuranceGroupOptions}
            selected={draft.typeOfInsuranceGroupIds}
            onChange={(value) => set('typeOfInsuranceGroupIds', value)}
            disableWhenEmpty={false}
          />
        </FilterField>
        <FilterField label='Business Type'>
          <MultiSelect
            placeholder='Select…'
            options={BUSINESS_TYPE_OPTIONS}
            selected={draft.businessTypes}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                businessTypes: value,
                // Mirrors web clearDependentFields for TOI / Sub-TOI.
                typeOfInsurances: [],
                subTypeOfInsurances: []
              }))
            }
          />
        </FilterField>
        <FilterField label='Type Of Insurance'>
          <MultiSelect
            placeholder='Select…'
            options={toiOptions}
            selected={draft.typeOfInsurances}
            onChange={(value) => set('typeOfInsurances', value)}
          />
        </FilterField>
        <FilterField label='Sub-Type Of Insurance'>
          <MultiSelect
            placeholder='Select…'
            options={subToiOptions}
            selected={draft.subTypeOfInsurances}
            onChange={(value) => set('subTypeOfInsurances', value)}
          />
        </FilterField>
      </div>

      <div className='ramp-scan-foot ramp-filters-foot'>
        <button
          type='button'
          className='ramp-scan-primary'
          onClick={() => {
            scan.setFilters(draft)
            onClose()
          }}
        >
          <Check size={16} /> Apply
        </button>
        <button type='button' className='ramp-scan-secondary' onClick={() => setDraft(EMPTY_FILTERS)}>
          <X size={16} /> Clear All
        </button>
      </div>
    </>
  )
}
